import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { 
  BarChart3Icon,
  CalendarIcon,
  ChevronRightIcon,
  ClockIcon,
  Trash2,
  Check
} from 'lucide-react';

function Dashboards() {
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const { notify } = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboards = async () => {
      try {
        setLoading(true);
        const data = await dashboardService.getDashboards();
        
        // Ensure data is an array before setting it
        if (Array.isArray(data)) {
          setDashboards(data);
        } else {
          console.error('Dashboards data is not an array:', data);
          setDashboards([]);
        }
      } catch (error) {
        console.error('Error fetching dashboards:', error);
        setDashboards([]);
        
        if (!error.message || !error.message.includes('auth')) {
          notify.error('Failed to load dashboards. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboards();
  }, []);

  // Filter dashboards based on search term
  const filteredDashboards = Array.isArray(dashboards)
    ? dashboards.filter(dashboard =>
        dashboard && dashboard.name ? dashboard.name.toLowerCase().includes(searchTerm.toLowerCase()) : false
      )
    : [];

  // Handle dashboard click - navigate to dashboard detail page
  const handleDashboardClick = (dashboard) => {
    if (dashboard.status === 'progressing') {
      return; // Don't navigate for progressing dashboards
    }
    
    const dashboardUuid = dashboard.uuid;
    
    console.log('Dashboard clicked:', dashboard);
    console.log('Dashboard UUID for navigation:', dashboardUuid);
    console.log('Navigating to:', `/dashboards/${dashboardUuid}`);
    navigate(`/dashboards/${dashboardUuid}`);
  };

  // Handle dashboard deletion
  const handleDeleteDashboard = async (dashboardUuid) => {
    // TODO: Implement dashboard deletion when backend endpoint is available
    // This should call dashboardService.deleteDashboard(dashboardUuid)
    // and remove the dashboard from local state on success
    try {
      // Reset confirmation state
      setShowDeleteConfirm(null);
      
      // TODO: Uncomment when backend endpoint exists
      // await dashboardService.deleteDashboard(dashboardUuid);
      // setDashboards(prev => prev.filter(dashboard => dashboard.uuid !== dashboardUuid));
      // notify.success('Dashboard deleted successfully');
      
      // Placeholder notification for now
      notify.info('Dashboard deletion not yet implemented');
      
    } catch (error) {
      console.error('Delete dashboard error:', error);
      // TODO: Add proper error handling when backend is implemented
      notify.error('Failed to delete dashboard');
    }
  };

  // Format date if available
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch (e) {
      return dateStr;
    }
  };

  // Get clean status text
  const getStatusText = (status) => {
    switch (status) {
      case 'ready':
        return 'Ready';
      case 'progressing':
        return 'Progressing';
      default:
        return 'Unknown';
    }
  };

  // Get status styling classes
  const getStatusClasses = (status) => {
    switch (status) {
      case 'ready':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400';
      case 'progressing':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400';
      default:
        return 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300';
    }
  };

  // Calculate build progress based on elapsed time since changed_on_utc
  const calculateBuildProgress = (changedOnUtc) => {
    if (!changedOnUtc) return 0;
    
    try {
      // Handle timestamps that may or may not have 'Z' suffix for UTC
      let timestampString = changedOnUtc.trim();
      
      // If no timezone indicator, assume UTC and add 'Z'
      if (!timestampString.endsWith('Z') && 
          !timestampString.includes('+') && 
          !timestampString.includes('-', 10)) { // Don't match date separator
        timestampString += 'Z';
      }
      
      const changedTime = new Date(timestampString);
      const currentTime = Date.now();
      
      // Validate that the timestamp was parsed correctly
      if (isNaN(changedTime.getTime())) {
        console.error('Invalid timestamp parsed:', timestampString);
        return 0;
      }
      
      const minutesElapsed = Math.max(0, (currentTime - changedTime.getTime()) / (1000 * 60));
      
      console.log('Progress calculation:', {
        original: changedOnUtc,
        normalized: timestampString,
        changedTime: changedTime.toISOString(),
        currentTime: new Date(currentTime).toISOString(),
        minutesElapsed,
        progress: Math.min(90, (minutesElapsed / 8) * 100)
      });
      
      // Progress: 1 minute = 12.5%, 8 minutes = 100%, cap at 90%
      const progress = Math.min(90, (minutesElapsed / 8) * 100);
      return Math.round(progress);
    } catch (error) {
      console.error('Error calculating build progress:', error);
      return 0;
    }
  };

  // Calculate time remaining based on current progress
  const calculateTimeRemaining = (progress) => {
    if (progress >= 90) {
      return 'finishing up';
    }
    
    // Calculate minutes remaining based on 8-minute total build time
    const minutesRemaining = Math.max(0, 8 - (progress / 100 * 8));
    const roundedMinutes = Math.ceil(minutesRemaining);
    
    if (roundedMinutes <= 1) {
      return '< 1 minute left';
    }
    
    return `${roundedMinutes} minutes left`;
  };

  // Render progress bar for progressing dashboards
  const renderProgressBar = (progress) => (
    <div className="flex items-center gap-2 justify-end">
      <div className="w-16 bg-yellow-200 dark:bg-yellow-800/30 rounded-full h-2">
        <div 
          className="bg-yellow-500 dark:bg-yellow-400 h-2 rounded-full transition-all duration-300" 
          style={{ width: `${progress}%` }}
        ></div>
      </div>
      <span className="text-xs font-medium text-yellow-700 dark:text-yellow-400 min-w-[28px]">
        {progress}%
      </span>
      <div className="flex items-center gap-1 w-24">
        <ClockIcon className="w-3 h-3 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
        <span className="text-xs text-yellow-700 dark:text-yellow-400 whitespace-nowrap">
          {calculateTimeRemaining(progress)}
        </span>
      </div>
    </div>
  );

  // Render empty state
  const renderEmptyState = () => (
    <div className="text-center py-12">
      <BarChart3Icon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
      <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
        {searchTerm ? 'No dashboards found' : 'No dashboards available'}
      </h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {searchTerm 
          ? 'Try adjusting your search terms.' 
          : 'You don\'t have any dashboards yet.'}
      </p>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboards</h1>
      </div>

      {/* Search bar */}
      <div className="mb-6">
        <input
          type="text"
          className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-600 focus:border-transparent"
          placeholder="Search dashboards..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Dashboards content */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 mx-auto border-b-2 border-primary-600 dark:border-primary-500 rounded-full"></div>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading dashboards...</p>
        </div>
      ) : filteredDashboards.length > 0 ? (
        <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg">
          <div className="space-y-0">
            {filteredDashboards.map((dashboard, index) => (
              <div
                key={dashboard.url || index}
                className={`relative flex items-center justify-between p-4 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-all duration-200 group border-b border-slate-200 dark:border-slate-700 last:border-b-0 transform hover:scale-[1.003] hover:shadow-md hover:-translate-y-0.5 ${
                  dashboard.status === 'progressing' ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
                onClick={() => handleDashboardClick(dashboard)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-4">
                    {/* Name */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-slate-900 dark:text-white truncate">
                        {dashboard.name}
                      </h3>
                    </div>
                    
                    {/* Changed Date */}
                    <div className="text-sm text-slate-500 dark:text-slate-400 w-24 text-right flex-shrink-0">
                      {dashboard.changed_on_utc ? formatDate(dashboard.changed_on_utc) : 'Unknown'}
                    </div>
                    
                    {/* Status */}
                    <div className="w-48 flex justify-end flex-shrink-0">
                      {dashboard.status === 'progressing' ? (
                        renderProgressBar(calculateBuildProgress(dashboard.changed_on_utc))
                      ) : (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusClasses(dashboard.status)}`}>
                          {getStatusText(dashboard.status)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center ml-6">
                  <button
                    type="button"
                    className={`opacity-0 group-hover:opacity-100 transition-all duration-200 p-1 transform hover:scale-110 ${
                      showDeleteConfirm === dashboard.uuid
                        ? 'text-red-500 hover:text-red-600'
                        : 'text-slate-400 hover:text-red-500'
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (showDeleteConfirm === dashboard.uuid) {
                        // Second click - actually delete
                        handleDeleteDashboard(dashboard.uuid);
                      } else {
                        // First click - show confirm state
                        setShowDeleteConfirm(dashboard.uuid);
                        // Auto-reset after 3 seconds if not clicked
                        setTimeout(() => {
                          setShowDeleteConfirm(null);
                        }, 3000);
                      }
                    }}
                    title={showDeleteConfirm === dashboard.uuid ? "Click again to confirm delete" : "Delete dashboard"}
                  >
                    {showDeleteConfirm === dashboard.uuid ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        renderEmptyState()
      )}
    </div>
  );
}

export default Dashboards;